import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, CreditCard, Smartphone, Sparkles } from "lucide-react";
import UpiPayDialog from "@/components/UpiPayDialog";
import TalkToSalesDialog from "@/components/TalkToSalesDialog";
import { detectRegion, rememberRegion } from "@/lib/region";

export default function Pricing() {
  const [me, setMe] = useState(null);
  const [cat, setCat] = useState(null);
  const [region, setRegion] = useState(detectRegion);
  const [cycle, setCycle] = useState("monthly");
  const [seatsByPlan, setSeatsByPlan] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [upi, setUpi] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesPlan, setSalesPlan] = useState("");

  const loadCatalog = useCallback((r) => {
    api.get(`/billing/catalog?region=${r}`).then(({ data }) => {
      setCat(data);
      setSeatsByPlan((prev) => {
        const next = { ...prev };
        (data.plans || []).forEach((p) => { if (next[p.plan] == null) next[p.plan] = p.min_users || 1; });
        return next;
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    api.get("/billing/me").then(({ data }) => {
      setMe(data);
      const r = data.region === "INTL" ? "INTL" : "IN";
      setRegion(r);
      loadCatalog(r);
    }).catch(() => loadCatalog(detectRegion()));
  }, [loadCatalog]);

  useEffect(() => { loadCatalog(region); }, [loadCatalog, region]);

  function changeRegion(r) { setRegion(r); rememberRegion(r); loadCatalog(r); }

  const currency = region === "IN" ? "INR" : "USD";
  const sym = currency === "INR" ? "₹" : "$";
  const money = (a, dec = 2) => `${sym}${Number(a).toLocaleString(currency === "INR" ? "en-IN" : "en-US", { minimumFractionDigits: dec, maximumFractionDigits: 2 })}`;
  const isOrgAdmin = me?.role === "OrgAdmin";

  async function startPayment(plan) {
    if (!isOrgAdmin) { toast.error("Only your organisation admin can upgrade. Please ask them."); return; }
    const seats = seatsByPlan[plan];
    setBusyKey(plan);
    try {
      const { data: order } = await api.post("/billing/orders", { plan, billing_cycle: cycle, seats });
      if (region === "IN") {
        setUpi(order);
      } else {
        try {
          await api.post(`/billing/orders/${order.id}/cashfree/session`, { return_url: window.location.origin + "/billing" });
          toast.success("Redirecting to secure card checkout…");
        } catch (e) {
          if (e?.response?.status === 503) {
            toast.message("International card payments are launching soon.", { description: "Your order is saved. Please contact corporate@yanthralabs.com to complete payment." });
          } else {
            toast.error(formatApiError(e));
          }
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey("");
    }
  }

  if (!cat) return <div className="p-10 text-sm text-muted-foreground">Loading plans…</div>;

  return (
    <div data-testid="pricing-root" className="max-w-6xl mx-auto px-6 py-10">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full tone-powder text-[11px] font-semibold uppercase tracking-[0.18em] mb-4">
          <Sparkles className="w-3.5 h-3.5" /> Plans & Pricing
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">
          Scale your <span style={{ color: "hsl(var(--cyan))" }}>MeetIQ</span><span style={{ color: "hsl(var(--gold))" }}>360</span>
        </h1>
        <p className="text-muted-foreground mt-3 text-sm md:text-base max-w-xl mx-auto">
          Per-user pricing. India pays by UPI; other regions by card. {region === "IN" && "Prices shown are exclusive of 18% GST."}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
        <div className="inline-flex rounded-full border border-border p-1 bg-card">
          {[["IN", "India · ₹"], ["INTL", "Outside India · $"]].map(([v, l]) => (
            <button key={v} data-testid={`region-${v}`} onClick={() => changeRegion(v)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${region === v ? "text-white" : "text-foreground/70"}`}
              style={region === v ? { background: "hsl(var(--navy))" } : {}}>{l}</button>
          ))}
        </div>
        <div className="inline-flex rounded-full border border-border p-1 bg-card">
          {[["monthly", "Monthly"], ["annual", "Annual · save 20%"]].map(([v, l]) => (
            <button key={v} data-testid={`cycle-${v}`} onClick={() => setCycle(v)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${cycle === v ? "text-white" : "text-foreground/70"}`}
              style={cycle === v ? { background: "hsl(var(--cyan))" } : {}}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-10">
        {(cat.plans || []).map((p) => {
          const rate = p.prices[region]?.[cycle];
          const custom = rate == null;
          const featured = !!p.popular;
          const seats = seatsByPlan[p.plan] ?? p.min_users ?? 1;
          const months = cycle === "annual" ? 12 : 1;
          const subtotal = custom ? 0 : rate * seats * months;
          const gst = region === "IN" ? subtotal * (cat.gst_rate / 100) : 0;
          const total = subtotal + gst;
          return (
            <div key={p.plan} data-testid={`plan-card-${p.plan}`}
              className={`relative rounded-3xl border bg-card p-6 flex flex-col transition-transform hover:-translate-y-1 ${featured ? "border-transparent shadow-2xl ring-2" : "border-border shadow-sm"}`}
              style={featured ? { "--tw-ring-color": "hsl(var(--cyan))" } : {}}>
              {featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: "hsl(var(--gold))" }}>Most popular</div>
              )}
              <div className="text-sm font-bold uppercase tracking-[0.12em]">{p.name}</div>
              <div className="text-xs text-muted-foreground mt-1 h-8">{p.tagline}</div>
              <div className="mt-3 flex items-end gap-1">
                {custom ? (
                  <span className="font-heading text-3xl">Custom</span>
                ) : (
                  <>
                    <span className="font-heading text-4xl">{money(rate, 0)}</span>
                    <span className="text-xs text-muted-foreground mb-1.5">/ user / mo{cycle === "annual" ? " (billed yearly)" : ""}</span>
                  </>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">{p.min_users}{p.max_users ? `–${p.max_users}` : "+"} users</div>

              <ul className="mt-4 space-y-2.5 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "hsl(var(--cyan))" }} />
                    <span className="text-foreground/80">{f}</span>
                  </li>
                ))}
              </ul>

              {!custom && (
                <div className="mt-5 rounded-2xl bg-secondary/40 p-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Users</span>
                    <input data-testid={`seats-${p.plan}`} type="number" min={p.min_users || 1} max={p.max_users || 100000}
                      value={seats}
                      onChange={(e) => setSeatsByPlan((s) => ({ ...s, [p.plan]: Math.max(p.min_users || 1, Number(e.target.value) || 1) }))}
                      className="w-20 rounded-full border border-border bg-background px-3 py-1 text-center" />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{money(subtotal)}</span>
                  </div>
                  {region === "IN" && (
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">GST {cat.gst_rate}%</span><span className="font-semibold">{money(gst)}</span>
                    </div>
                  )}
                  <div className="mt-1 flex items-center justify-between text-sm pt-1 border-t border-border">
                    <span className="font-semibold">Total{cycle === "annual" ? " / yr" : " / mo"}</span>
                    <span data-testid={`total-${p.plan}`} className="font-heading text-lg">{money(total)}</span>
                  </div>
                </div>
              )}

              <div className="mt-5">
                {custom ? (
                  <button type="button" data-testid={`contact-${p.plan}`}
                    onClick={() => { setSalesPlan(p.name || p.plan); setSalesOpen(true); }}
                    className="w-full inline-flex items-center justify-center rounded-full border border-border bg-card py-3 text-sm font-semibold hover:bg-secondary transition-colors">Contact sales</button>
                ) : region === "IN" ? (
                  <button data-testid={`pay-upi-${p.plan}`} onClick={() => startPayment(p.plan)} disabled={busyKey === p.plan}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
                    {busyKey === p.plan ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />} Pay via UPI
                  </button>
                ) : (
                  <button data-testid={`pay-card-${p.plan}`} onClick={() => startPayment(p.plan)} disabled={busyKey === p.plan}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
                    {busyKey === p.plan ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay by card
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {region === "IN" && (
        <p className="text-center text-[11px] text-muted-foreground mt-6">GST tax invoice is emailed and available in your billing dashboard after verification.</p>
      )}

      {upi && <UpiPayDialog order={upi} onClose={() => setUpi(null)} />}
      <TalkToSalesDialog open={salesOpen} onOpenChange={setSalesOpen} plan={salesPlan} />
    </div>
  );
}
